import React, { useEffect, useRef, useState } from "react";
import { X, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { track } from "@/lib/analytics";

const STORAGE_KEY = "cm_promo_bar_dismissed_v1";

export default function PromoBar() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY) === "1";
    // Mostra após 600ms para não competir com o Hero
    const t = setTimeout(() => setVisible(!dismissed), 600);
    return () => clearTimeout(t);
  }, []);

  // Ajusta variável CSS que empurra a Navbar para baixo da PromoBar.
  // Mede o elemento real (a altura varia com fontes/quebra de linha).
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.setProperty("--promo-bar-height", "0px");
      return;
    }
    const measure = () => {
      const h = ref.current?.offsetHeight || 0;
      root.style.setProperty("--promo-bar-height", h + "px");
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && ref.current) ro.observe(ref.current);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
      root.style.setProperty("--promo-bar-height", "0px");
    };
  }, [visible]);

  const handleCtaClick = () => {
    track("cta_click", { location: "promo_bar", target: "livros" });
  };

  const dismiss = () => {
    track("promo_bar_dismissed");
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  return (
    <div
      ref={ref}
      data-testid="promo-bar"
      aria-hidden={!visible}
      className={`fixed top-0 inset-x-0 z-[60] transition-transform duration-500 ease-out ${
        visible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="bg-[#0B1E36] text-white border-b border-[#C5A059]/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 md:py-3 flex items-center gap-3 md:gap-6">
          <span
            className="hidden md:inline-flex h-2 w-2 rounded-full bg-[#C5A059] flex-shrink-0"
            aria-hidden
          />
          <p
            data-testid="promo-bar-text"
            className="flex-1 text-[0.78rem] md:text-[0.88rem] text-white/90 leading-snug font-light tracking-[0.01em]"
          >
            <span className="hidden sm:inline">
              Adquira um e-book e ajude famílias através desta obra.
            </span>
            <span className="sm:hidden">
              E-book que ajuda famílias.
            </span>
          </p>
          <Link
            to="/livros"
            onClick={handleCtaClick}
            data-testid="promo-bar-cta"
            className="flex-shrink-0 inline-flex items-center gap-1.5 bg-[#C5A059] hover:bg-[#D4B26B] text-[#0B1E36] text-[0.68rem] md:text-[0.72rem] uppercase tracking-[0.18em] md:tracking-[0.22em] font-medium px-3 md:px-5 py-2 md:py-2.5 transition-colors duration-300"
          >
            Quero adquirir agora
            <ArrowUpRight size={12} strokeWidth={1.8} className="hidden sm:inline" />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            data-testid="promo-bar-close"
            aria-label="Fechar barra promocional"
            className="flex-shrink-0 text-white/60 hover:text-white transition-colors duration-300 p-1"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
